import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { functions } from './firebase';
import { httpsCallable } from 'firebase/functions';

function PlayerRegistration() {
  const navigate = useNavigate();
  
  // Step tracking
  const [step, setStep] = useState(1); // 1: Search, 1.5: Select from list, 2: Confirm, 3: Details, 4: Payment, 5: Success
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchResults, setSearchResults] = useState([]); // Multiple results
  
  // Player verification state
  const [verifiedPlayer, setVerifiedPlayer] = useState(null);
  
  // Contact details state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  
  // Payment/registration state
  const [registering, setRegistering] = useState(false);
  const [registrationError, setRegistrationError] = useState('');

  // Search for CueScore player
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchError('Please enter a name or CueScore URL');
      return;
    }
    
    setSearching(true);
    setSearchError('');
    setVerifiedPlayer(null);
    setSearchResults([]);
    
    try {
      const verifyPlayer = httpsCallable(functions, 'verifyPlayerCueScore');
      const result = await verifyPlayer({ searchQuery: searchQuery.trim() });
      
      if (result.data.found) {
        if (result.data.multiple) {
          // Multiple results - show selection list
          setSearchResults(result.data.players);
          setStep(1.5);
        } else {
          // Single result - go straight to confirmation
          setVerifiedPlayer(result.data.players[0]);
          setStep(2);
        }
      } else {
        setSearchError('Player not found. Please check the name or try a CueScore profile URL.');
      }
    } catch (err) {
      console.error('Search error:', err);
      setSearchError('Failed to search. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  // Select a player from multiple results
  const handleSelectPlayer = (player) => {
    setVerifiedPlayer(player);
    setStep(2);
  };

  // Confirm this is the correct player
  const handleConfirmPlayer = () => {
    setStep(3);
  };

  // Go back to search
  const handleSearchAgain = () => {
    setVerifiedPlayer(null);
    setSearchQuery('');
    setStep(1);
  };

  // Submit contact details and register directly (FREE)
  const handleSubmitDetails = async () => {
    if (!phoneNumber.trim()) {
      setRegistrationError('Please enter your phone number');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setRegistrationError('Please enter a valid email address');
      return;
    }
    setRegistrationError('');
    setRegistering(true);
    
    try {
      const registerPlayer = httpsCallable(functions, 'registerPremiumPlayer');
      const result = await registerPlayer({
        cueScorePlayerId: verifiedPlayer.playerId,
        cueScoreName: verifiedPlayer.name,
        cueScoreUrl: verifiedPlayer.url,
        cueScoreImage: verifiedPlayer.image || '',
        phoneNumber: phoneNumber.trim(),
        email: email.trim(),
        paymentMethod: 'free'
      });
      
      if (result.data.success) {
        setStep(5);
      } else {
        setRegistrationError('Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setRegistrationError('Registration failed. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  // Handle payment via Stripe Checkout
  const handleSubscribe = async () => {
    setRegistering(true);
    setRegistrationError('');
    
    try {
      const createCheckout = httpsCallable(functions, 'createStripeCheckout');
      const result = await createCheckout({
        cueScorePlayerId: verifiedPlayer.playerId,
        cueScoreName: verifiedPlayer.name,
        cueScoreUrl: verifiedPlayer.url,
        cueScoreImage: verifiedPlayer.image || '',
        phoneNumber: phoneNumber.trim(),
        email: email.trim(),
        successUrl: `${window.location.origin}/registration-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/player-registration?cancelled=true`,
      });
      
      if (result.data.success && result.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = result.data.url;
      } else {
        setRegistrationError('Failed to create checkout session. Please try again.');
        setRegistering(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setRegistrationError('Failed to start payment. Please try again.');
      setRegistering(false);
    }
    // Note: Don't setRegistering(false) on success - we're redirecting
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#111827',
      color: 'white',
      padding: '48px 24px',
    },
    content: {
      maxWidth: '500px',
      margin: '0 auto',
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: '#9ca3af',
      fontSize: '16px',
      cursor: 'pointer',
      marginBottom: '24px',
      padding: '8px 0',
    },
    header: {
      textAlign: 'center',
      marginBottom: '32px',
    },
    title: {
      fontSize: '32px',
      fontWeight: 'bold',
      marginBottom: '16px',
    },
    subtitle: {
      fontSize: '16px',
      color: '#9ca3af',
      lineHeight: '1.6',
    },
    card: {
      backgroundColor: '#1f2937',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
    },
    featureList: {
      listStyle: 'none',
      padding: 0,
      margin: '24px 0',
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '12px',
      fontSize: '16px',
    },
    checkmark: {
      color: '#10b981',
      fontSize: '18px',
    },
    price: {
      textAlign: 'center',
      marginBottom: '24px',
    },
    priceAmount: {
      fontSize: '36px',
      fontWeight: 'bold',
      color: '#10b981',
    },
    priceLabel: {
      fontSize: '16px',
      color: '#9ca3af',
    },
    freeNote: {
      backgroundColor: '#1e3a5f',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '24px',
      fontSize: '14px',
      lineHeight: '1.5',
      borderLeft: '4px solid #3b82f6',
    },
    inputGroup: {
      marginBottom: '16px',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      color: '#9ca3af',
    },
    input: {
      width: '100%',
      padding: '14px 16px',
      backgroundColor: '#374151',
      border: '1px solid #4b5563',
      borderRadius: '8px',
      color: 'white',
      fontSize: '16px',
      boxSizing: 'border-box',
    },
    button: {
      width: '100%',
      padding: '16px',
      backgroundColor: '#2563eb',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '18px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    buttonDisabled: {
      width: '100%',
      padding: '16px',
      backgroundColor: '#374151',
      color: '#6b7280',
      border: 'none',
      borderRadius: '8px',
      fontSize: '18px',
      fontWeight: 'bold',
      cursor: 'not-allowed',
    },
    secondaryButton: {
      width: '100%',
      padding: '14px',
      backgroundColor: 'transparent',
      color: '#9ca3af',
      border: '1px solid #4b5563',
      borderRadius: '8px',
      fontSize: '16px',
      cursor: 'pointer',
      marginTop: '12px',
    },
    error: {
      backgroundColor: '#7f1d1d',
      color: '#fca5a5',
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px',
    },
    playerCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      backgroundColor: '#374151',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '24px',
    },
    playerImage: {
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      backgroundColor: '#4b5563',
      objectFit: 'cover',
    },
    playerInfo: {
      flex: 1,
    },
    playerName: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '4px',
    },
    playerCountry: {
      fontSize: '14px',
      color: '#9ca3af',
    },
    successIcon: {
      fontSize: '64px',
      textAlign: 'center',
      marginBottom: '24px',
    },
    appLinks: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'center',
      marginTop: '24px',
    },
    appBadge: {
      height: '44px',
      cursor: 'pointer',
    },
    stepIndicator: {
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
      marginBottom: '32px',
    },
    stepDot: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: '#4b5563',
    },
    stepDotActive: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: '#2563eb',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        
        {/* Back button */}
        <button onClick={() => navigate('/')} style={styles.backButton}>
          ← Back to Home
        </button>

        {/* Step indicator */}
        <div style={styles.stepIndicator}>
          {[1, 2, 3, 4, 5].map(s => (
            <div key={s} style={s <= step ? styles.stepDotActive : styles.stepDot} />
          ))}
        </div>

        {/* Step 1: Search */}
        {step === 1 && (
          <>
            <div style={styles.header}>
              <h1 style={styles.title}>🔔 Never Miss a Match</h1>
              <p style={styles.subtitle}>
                Get instant notifications when your matches are called across any CueScore tournament.
              </p>
            </div>

            <ul style={styles.featureList}>
              <li style={styles.featureItem}>
                <span style={styles.checkmark}>✓</span>
                Works with any tournament on CueScore
              </li>
              <li style={styles.featureItem}>
                <span style={styles.checkmark}>✓</span>
                Push notifications direct to your phone
              </li>
              <li style={styles.featureItem}>
                <span style={styles.checkmark}>✓</span>
                Know exactly when and where to play
              </li>
              <li style={styles.featureItem}>
                <span style={styles.checkmark}>✓</span>
                <strong>100% Free!</strong>
              </li>
            </ul>

            {/* Instructions Card */}
            <div style={{
              backgroundColor: '#1e3a5f',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              border: '1px solid #2563eb'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#60a5fa' }}>
                📋 How to find your CueScore profile URL
              </h3>
              <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#d1d5db', lineHeight: '1.8' }}>
                <li>Go to <a href="https://cuescore.com" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>cuescore.com</a> and log in</li>
                <li>Copy the URL from your browser's address bar</li>
                <li>Paste it below!</li>
              </ol>
              <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '12px', marginBottom: 0 }}>
                Example: <code style={{ backgroundColor: '#374151', padding: '2px 6px', borderRadius: '4px' }}>https://cuescore.com/player/John+Smith/12345678</code>
              </p>
            </div>

            <div style={styles.card}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Enter your CueScore name or profile URL</label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="e.g. John Smith or https://cuescore.com/player/..."
                  style={styles.input}
                />
              </div>

              {searchError && (
                <div style={styles.error}>{searchError}</div>
              )}

              <button
                onClick={handleSearch}
                disabled={searching}
                style={searching ? styles.buttonDisabled : styles.button}
              >
                {searching ? 'Searching...' : 'Find My Profile'}
              </button>
            </div>
          </>
        )}

        {/* Step 1.5: Select from Multiple Results */}
        {step === 1.5 && searchResults.length > 0 && (
          <>
            <div style={styles.header}>
              <h1 style={styles.title}>Multiple Players Found</h1>
              <p style={styles.subtitle}>
                We found {searchResults.length} players matching "{searchQuery}". Please select your profile:
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              {searchResults.map((player, index) => (
                <button
                  key={player.playerId}
                  onClick={() => handleSelectPlayer(player)}
                  style={{
                    ...styles.playerCard,
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    transition: 'border-color 0.2s',
                    marginBottom: 0
                  }}
                  onMouseOver={(e) => e.currentTarget.style.borderColor = '#2563eb'}
                  onMouseOut={(e) => e.currentTarget.style.borderColor = 'transparent'}
                >
                  {player.image ? (
                    <img src={player.image} alt={player.name} style={styles.playerImage} />
                  ) : (
                    <div style={styles.playerImage} />
                  )}
                  <div style={styles.playerInfo}>
                    <div style={styles.playerName}>{player.name}</div>
                    {player.country && (
                      <div style={styles.playerCountry}>📍 {player.country}</div>
                    )}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '14px' }}>Select →</div>
                </button>
              ))}
            </div>

            <button onClick={handleSearchAgain} style={styles.secondaryButton}>
              ← Search again
            </button>
          </>
        )}

        {/* Step 2: Confirm Player */}
        {step === 2 && verifiedPlayer && (
          <>
            <div style={styles.header}>
              <h1 style={styles.title}>Is this you?</h1>
              <p style={styles.subtitle}>
                Please confirm this is your CueScore profile
              </p>
            </div>

            <div style={styles.playerCard}>
              {verifiedPlayer.image ? (
                <img src={verifiedPlayer.image} alt={verifiedPlayer.name} style={styles.playerImage} />
              ) : (
                <div style={styles.playerImage} />
              )}
              <div style={styles.playerInfo}>
                <div style={styles.playerName}>{verifiedPlayer.name}</div>
                {verifiedPlayer.country && (
                  <div style={styles.playerCountry}>📍 {verifiedPlayer.country}</div>
                )}
              </div>
            </div>

            <button onClick={handleConfirmPlayer} style={styles.button}>
              ✓ Yes, this is me
            </button>

            <button onClick={handleSearchAgain} style={styles.secondaryButton}>
              ✕ No, search again
            </button>
          </>
        )}

        {/* Step 3: Contact Details */}
        {step === 3 && (
          <>
            <div style={styles.header}>
              <h1 style={styles.title}>Your Details</h1>
              <p style={styles.subtitle}>
                We need your contact details to send notifications
              </p>
            </div>

            <div style={styles.playerCard}>
              {verifiedPlayer?.image ? (
                <img src={verifiedPlayer.image} alt={verifiedPlayer.name} style={styles.playerImage} />
              ) : (
                <div style={styles.playerImage} />
              )}
              <div style={styles.playerInfo}>
                <div style={styles.playerName}>{verifiedPlayer?.name}</div>
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Mobile Number *</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+44 7123 456789"
                  style={styles.input}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Email Address *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  style={styles.input}
                />
              </div>

              {registrationError && (
                <div style={styles.error}>{registrationError}</div>
              )}

              <button 
                onClick={handleSubmitDetails} 
                disabled={registering}
                style={registering ? styles.buttonDisabled : styles.button}
              >
                {registering ? 'Registering...' : '🔔 Register for Free Notifications'}
              </button>

              <button onClick={() => setStep(2)} style={styles.secondaryButton}>
                ← Back
              </button>
            </div>
          </>
        )}

        {/* Step 4: Payment */}
        {step === 4 && (
          <>
            <div style={styles.header}>
              <h1 style={styles.title}>Complete Subscription</h1>
              <p style={styles.subtitle}>
                Subscribe to receive match notifications for 1 year
              </p>
            </div>

            <div style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Annual Subscription</div>
                  <div style={{ fontSize: '14px', color: '#9ca3af' }}>Match notifications for all CueScore tournaments</div>
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>£9.99</div>
              </div>

              {/* Player Summary */}
              <div style={{ 
                backgroundColor: '#374151', 
                borderRadius: '8px', 
                padding: '16px', 
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                {verifiedPlayer?.image ? (
                  <img src={verifiedPlayer.image} alt="" style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#4b5563' }} />
                )}
                <div>
                  <div style={{ fontWeight: 'bold' }}>{verifiedPlayer?.name}</div>
                  <div style={{ fontSize: '14px', color: '#9ca3af' }}>{email}</div>
                </div>
              </div>

              {/* What's included */}
              <div style={{ borderTop: '1px solid #4b5563', paddingTop: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '12px' }}>What's included:</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '14px' }}>
                  <li style={{ marginBottom: '8px' }}>✅ Push notifications when your match is assigned</li>
                  <li style={{ marginBottom: '8px' }}>✅ Works at any CueScore tournament</li>
                  <li style={{ marginBottom: '8px' }}>✅ 1 year subscription</li>
                </ul>
              </div>

              {registrationError && (
                <div style={styles.error}>{registrationError}</div>
              )}

              <button
                onClick={handleSubscribe}
                disabled={registering}
                style={registering ? styles.buttonDisabled : { ...styles.button, backgroundColor: '#10b981' }}
              >
                {registering ? 'Redirecting to payment...' : '💳 Pay £9.99 with Stripe'}
              </button>

              <p style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', marginTop: '12px' }}>
                🔒 Secure payment powered by Stripe
              </p>

              <button onClick={() => setStep(3)} style={styles.secondaryButton}>
                ← Back
              </button>
            </div>
          </>
        )}

        {/* Step 5: Success */}
        {step === 5 && (
          <>
            <div style={styles.successIcon}>🎉</div>
            
            <div style={styles.header}>
              <h1 style={styles.title}>You're All Set!</h1>
              <p style={styles.subtitle}>
                Your subscription is active. Now download our app to receive notifications.
              </p>
            </div>

            <div style={styles.card}>
              <h3 style={{ marginBottom: '16px', textAlign: 'center' }}>Download the App</h3>
              <p style={{ fontSize: '14px', color: '#9ca3af', textAlign: 'center', marginBottom: '16px' }}>
                Install the Cue Clock Pro app and sign in with your phone number to start receiving match notifications.
              </p>
              
              <div style={styles.appLinks}>
                <a href="https://apps.apple.com/gb/app/cue-clock-pro/id6758524500" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                    alt="Download on App Store" 
                    style={styles.appBadge}
                  />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.cueclockpro.playerapp" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                    alt="Get it on Google Play" 
                    style={styles.appBadge}
                  />
                </a>
              </div>
            </div>

            <button onClick={() => navigate('/')} style={styles.button}>
              Return to Home
            </button>
          </>
        )}

      </div>
    </div>
  );
}

export default PlayerRegistration;